import {
  AppBar,
  Avatar,
  Box,
  Button,
  IconButton,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material"

import MenuIcon from "@mui/icons-material/Menu"

import {
  useLocation,
  useNavigate,
} from "react-router-dom"

import {
  getBusinessLabel,
  getDashboardConfig,
} from "../config/appConfig"


// =========================================================
// BUSINESS LABELS
// =========================================================

const MAIN_DISTRIBUTOR =
  getBusinessLabel(
    "mainDistributor",
    "Distributeur principal"
  )

const RELAY_DEPOT =
  getBusinessLabel(
    "relayDepot",
    "Structure secondaire"
  )

const SHOP =
  getBusinessLabel(
    "shop",
    "Shop"
  )

const SHOPS =
  getBusinessLabel(
    "shops",
    "Shops"
  )

const WAREHOUSES =
  getBusinessLabel(
    "warehouses",
    "Entrepôts"
  )

const FIELD_AGENT =
  getBusinessLabel(
    "fieldAgent",
    "Agent commercial"
  )

const FIELD_AGENTS =
  getBusinessLabel(
    "fieldAgents",
    "Agents commerciaux"
  )


const getDashboardTitle = (
  key,
  fallback
) =>
  getDashboardConfig(key)?.label ||
  fallback


// =========================================================
// PAGE TITLES
// =========================================================

const pageTitles = {
  "/": getDashboardTitle(
    "global",
    "Dashboard"
  ),

  "/dashboard": getDashboardTitle(
    "global",
    "Dashboard"
  ),

  "/dashboard-pro": getDashboardTitle(
    "pro",
    "Dashboard PRO"
  ),

  "/maison-djo-dashboard":
    getDashboardTitle(
      "distributor",
      `Dashboard ${MAIN_DISTRIBUTOR}`
    ),

  "/depot-relais-dashboard":
    getDashboardTitle(
      "relay",
      `Dashboard ${RELAY_DEPOT}`
    ),

  "/agent-commercial-dashboard":
    getDashboardTitle(
      "agents",
      `Dashboard ${FIELD_AGENTS}`
    ),

  "/warehouse-dashboard":
    getDashboardTitle(
      "warehouses",
      `Dashboard ${WAREHOUSES}`
    ),

  "/logistics-dashboard":
    getDashboardTitle(
      "logistics",
      "Dashboard Logistique"
    ),

  "/reporting":
    getDashboardTitle(
      "reporting",
      "Reporting"
    ),

  "/organizations":
    "Organisations",

  "/products":
    "Produits",

  "/categories":
    "Catégories",

  "/stores":
    `${SHOPS} / Points de vente`,

  "/warehouses":
    `Gestion ${WAREHOUSES.toLowerCase()}`,

  "/customers":
    "Clients",

  "/sales/new":
    "Nouvelle vente",

  "/sales":
    "Ventes",

  "/suppliers":
    "Fournisseurs",

  "/purchase-requests":
    "Demandes d'achat",

  "/purchase-orders":
    "Bons de commande",

  "/purchase-receipts":
    "Réceptions fournisseurs",

  "/supplier-invoices":
    "Factures fournisseurs",

  "/supplier-payments":
    "Paiements fournisseurs",

  "/production/boms":
    "Nomenclatures (BOM)",

  "/production/orders":
    "Ordres de production",

  "/hr/dashboard":
    "Dashboard RH",

  "/hr/employees":
    "Employés",

  "/hr/departments-positions":
    "Départements & postes",

  "/hr/attendance":
    "Présences",

  "/hr/leaves":
    "Congés & permissions",

  "/hr/payroll":
    "Paie",

  "/hr/contracts-documents":
    "Contrats & documents",

  "/hr/performance":
    "Évaluation & performance",

  "/accounting/dashboard":
    "Dashboard Finance",

  "/accounting/chart-of-accounts":
    "Plan comptable",

  "/accounting/journal-entries":
    "Écritures comptables",

  "/accounting/general-ledger":
    "Grand livre",

  "/accounting/trial-balance":
    "Balance comptable",

  "/payments":
    "Paiements clients",

  "/deposits":
    "Dépôts caisse",

  "/expenses":
    "Dépenses",

  "/debts":
    "Dettes",

  "/debt-payments":
    "Paiement dettes",

  "/stock-orders":
    "Commandes stock",

  "/stock-balances":
    "Stock actuel",

  "/stock-alerts":
    "Alertes stock",

  "/stock-movements":
    "Mouvements stock",

  "/stock-transfers":
    "Transferts stock",

  "/stock-audits":
    "Audits stock",

  "/vehicles":
    "Véhicules",

  "/drivers":
    "Chauffeurs",

  "/delivery-trips":
    "Tournées / Livraisons",

  "/settings/product-prices":
    "Prix produits",

  "/store-reconciliations":
    "Rapprochements",

  "/roles":
    "Rôles",

  "/users":
    "Utilisateurs",

  "/activity-logs":
    getDashboardTitle(
      "activityLogs",
      "Journal d'activité"
    ),

  "/login":
    "Connexion",
}


// =========================================================
// MODULE DEFINITIONS
// =========================================================

const moduleDefinitions = [
  {
    title:
      "Pilotage",

    paths: [
      "/",
      "/dashboard",
      "/dashboard-pro",
      "/maison-djo-dashboard",
      "/depot-relais-dashboard",
      "/agent-commercial-dashboard",
      "/warehouse-dashboard",
      "/logistics-dashboard",
      "/reporting",
      "/activity-logs",
    ],
  },

  {
    title:
      "Structure",

    paths: [
      "/customers",
      "/stores",
    ],
  },

  {
    title:
      "Ventes",

    paths: [
      "/sales",
    ],
  },

  {
    title:
      "Achats & Fournisseurs",

    paths: [
      "/suppliers",
      "/purchase-requests",
      "/purchase-orders",
      "/purchase-receipts",
      "/supplier-invoices",
      "/supplier-payments",
    ],
  },

  {
    title:
      "Production",

    paths: [
      "/production",
    ],
  },

  {
    title:
      "Ressources humaines",

    paths: [
      "/hr",
    ],
  },

  {
    title:
      "Comptabilité & Finance",

    paths: [
      "/accounting",
    ],
  },

  {
    title:
      "Paiements & Caisse",

    paths: [
      "/payments",
      "/deposits",
      "/store-reconciliations",
    ],
  },

  {
    title:
      "Dépenses & Dettes",

    paths: [
      "/expenses",
      "/debts",
      "/debt-payments",
    ],
  },

  {
    title:
      "Stock & Inventaire",

    paths: [
      "/warehouses",
      "/stock-orders",
      "/stock-balances",
      "/stock-alerts",
      "/stock-movements",
      "/stock-transfers",
      "/stock-audits",
    ],
  },

  {
    title:
      "Logistique",

    paths: [
      "/vehicles",
      "/drivers",
      "/delivery-trips",
    ],
  },

  {
    title:
      "Catalogue",

    paths: [
      "/categories",
      "/products",
    ],
  },

  {
    title:
      "Paramètres",

    paths: [
      "/settings",
    ],
  },

  {
    title:
      "Administration",

    paths: [
      "/organizations",
      "/roles",
      "/users",
    ],
  },
]


// =========================================================
// ROLE LABELS
// =========================================================

const roleLabels = {
  platform_admin:
    "Platform Admin",

  super_admin:
    "Super Admin",

  dg:
    "Direction",

  admin_maison_djo:
    `Admin ${MAIN_DISTRIBUTOR}`,

  gestionnaire_maison_djo:
    `Gestionnaire ${MAIN_DISTRIBUTOR}`,

  stock_maison_djo:
    `Stock ${MAIN_DISTRIBUTOR}`,

  livreur_maison_djo:
    `Livraison ${MAIN_DISTRIBUTOR}`,

  admin_depot_relais:
    `Admin ${RELAY_DEPOT}`,

  gestionnaire_depot_relais:
    `Gestionnaire ${RELAY_DEPOT}`,

  stock_depot_relais:
    `Stock ${RELAY_DEPOT}`,

  gerant_depot_relais:
    `Gérant ${RELAY_DEPOT}`,

  gerant_magasin:
    `Gérant / Caissier ${SHOP}`,

  agent_commercial:
    FIELD_AGENT,

  caissier_central:
    "Caissier central",

  gestionnaire_operations:
    "Opérations + Finance",

  gestionnaire_stock:
    "Stock + Audit",

  controleur_stock:
    "Contrôleur stock",

  viewer:
    "Lecture seule",
}


// =========================================================
// HELPERS
// =========================================================

const isRouteMatch = (
  pathname,
  path
) => {
  if (path === "/") {
    return (
      pathname === "/"
    )
  }

  return (
    pathname === path ||
    pathname.startsWith(
      `${path}/`
    )
  )
}


const getCurrentModule = (
  pathname
) => {
  let bestMatch =
    null

  moduleDefinitions.forEach(
    (module) => {
      module.paths.forEach(
        (path) => {
          if (
            !isRouteMatch(
              pathname,
              path
            )
          ) {
            return
          }

          const score =
            path.length

          if (
            !bestMatch ||
            score >
              bestMatch.score
          ) {
            bestMatch = {
              title:
                module.title,

              score,
            }
          }
        }
      )
    }
  )

  return (
    bestMatch?.title ||
    "Pilotage"
  )
}


const getCurrentPageTitle = (
  pathname
) => {
  if (
    pageTitles[pathname]
  ) {
    return (
      pageTitles[pathname]
    )
  }

  const matchingPath =
    Object.keys(
      pageTitles
    )
      .filter(
        (path) =>
          path !== "/" &&
          isRouteMatch(
            pathname,
            path
          )
      )
      .sort(
        (a, b) =>
          b.length -
          a.length
      )[0]

  return (
    pageTitles[
      matchingPath
    ] ||
    "Dashboard"
  )
}


const normalizeText = (
  value
) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(
      /\s+/g,
      " "
    )


const getInitial = (
  value
) => {
  const cleanValue =
    String(value || "")
      .trim()

  return (
    cleanValue
      .charAt(0)
      .toUpperCase() ||
    "U"
  )
}


// =========================================================
// TOPBAR
// =========================================================

function Topbar({
  drawerWidth,
  onMobileMenuClick,
}) {
  const location =
    useLocation()

  const navigate =
    useNavigate()

  const role =
    localStorage.getItem(
      "kongosys_role"
    ) || ""

  const fullName =
    localStorage.getItem(
      "kongosys_full_name"
    ) || "Utilisateur"

  const roleLabel =
    roleLabels[role] ||
    role ||
    "Utilisateur"

  const showRoleLabel =
    normalizeText(
      fullName
    ) !==
    normalizeText(
      roleLabel
    )

  const moduleTitle =
    getCurrentModule(
      location.pathname
    )

  const pageTitle =
    getCurrentPageTitle(
      location.pathname
    )


  const clearSession =
    () => {
      localStorage.removeItem(
        "kongosys_user"
      )

      localStorage.removeItem(
        "kongosys_user_id"
      )

      localStorage.removeItem(
        "kongosys_full_name"
      )

      localStorage.removeItem(
        "kongosys_role"
      )

      localStorage.removeItem(
        "kongosys_role_id"
      )

      localStorage.removeItem(
        "kongosys_company_id"
      )

      localStorage.removeItem(
        "kongosys_organization_id"
      )

      localStorage.removeItem(
        "kongosys_organization_code"
      )

      localStorage.removeItem(
        "kongosys_organization_name"
      )

      localStorage.removeItem(
        "kongosys_organization_type"
      )

      localStorage.removeItem(
        "kongosys_store_id"
      )

      localStorage.removeItem(
        "kongosys_store_code"
      )

      localStorage.removeItem(
        "kongosys_store_name"
      )

      localStorage.removeItem(
        "kongosys_warehouse_id"
      )

      localStorage.removeItem(
        "kongosys_warehouse_code"
      )

      localStorage.removeItem(
        "kongosys_warehouse_name"
      )

      localStorage.removeItem(
        "kongosys_remember"
      )
    }


  const handleLogout =
    () => {
      clearSession()

      navigate(
        "/login",
        {
          replace: true,
        }
      )
    }


  return (
    <AppBar
      position="fixed"
      elevation={0}

      sx={{
        width: {
          xs: "100%",

          lg:
            `calc(100% - ${drawerWidth}px)`,
        },

        ml: {
          xs: 0,

          lg:
            `${drawerWidth}px`,
        },

        background:
          "linear-gradient(90deg, #0f172a 0%, #172554 58%, #1e3a8a 100%)",

        borderBottom:
          "1px solid rgba(255,255,255,0.08)",

        boxShadow:
          "0 4px 18px rgba(15,23,42,0.13)",

        zIndex:
          (theme) =>
            theme.zIndex.drawer +
            1,
      }}
    >
      <Toolbar
        sx={{
          minHeight: {
            xs: 58,
            sm: 62,
            lg: 66,
          },

          px: {
            xs: 1,
            sm: 1.5,
            md: 2,
            lg: 2.5,
          },

          gap: {
            xs: 0.8,
            sm: 1.1,
          },
        }}
      >
        {/* ================================================= */}
        {/* MOBILE / TABLET / POS MENU */}
        {/* ================================================= */}

        <IconButton
          color="inherit"
          onClick={
            onMobileMenuClick
          }

          aria-label=
            "Ouvrir le menu"

          sx={{
            display: {
              xs: "inline-flex",
              lg: "none",
            },

            flexShrink: 0,

            width: 40,
            height: 40,

            border:
              "1px solid rgba(255,255,255,0.14)",

            borderRadius:
              2,
          }}
        >
          <MenuIcon />
        </IconButton>


        {/* ================================================= */}
        {/* CONTEXT */}
        {/* ================================================= */}

        <Box
          sx={{
            flexGrow: 1,

            minWidth: 0,
          }}
        >
          <Typography
            noWrap

            sx={{
              color:
                "#93c5fd",

              fontSize: {
                xs: 10.5,
                sm: 11,
                md: 11.5,
              },

              fontWeight:
                900,

              lineHeight:
                1.05,

              letterSpacing:
                0.35,

              textTransform:
                "uppercase",
            }}
          >
            {moduleTitle}
          </Typography>

          <Typography
            noWrap

            sx={{
              mt: 0.35,

              color:
                "#fff",

              fontSize: {
                xs: 15,
                sm: 16,
                md: 17,
              },

              lineHeight:
                1.12,

              fontWeight:
                900,

              maxWidth: {
                xs: 160,
                sm: 360,
                md: 520,
                lg: 650,
              },
            }}
          >
            {pageTitle}
          </Typography>
        </Box>


        {/* ================================================= */}
        {/* CONNECTED USER */}
        {/* ================================================= */}

        <Stack
          direction="row"

          spacing={{
            xs: 0.5,
            sm: 0.8,
          }}

          alignItems="center"

          sx={{
            flexShrink: 0,

            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display:
                "flex",

              alignItems:
                "center",

              gap: {
                xs: 0,
                sm: 0.85,
              },

              minWidth: 0,

              maxWidth: {
                xs: 44,
                sm: 220,
                md: 270,
              },

              px: {
                xs: 0,
                sm: 0.7,
              },

              py: {
                xs: 0,
                sm: 0.45,
              },

              borderRadius:
                999,

              backgroundColor: {
                xs: "transparent",
                sm: "rgba(255,255,255,0.08)",
              },

              border: {
                xs: "none",
                sm: "1px solid rgba(255,255,255,0.10)",
              },
            }}
          >
            <Avatar
              sx={{
                width: {
                  xs: 36,
                  sm: 34,
                },

                height: {
                  xs: 36,
                  sm: 34,
                },

                bgcolor:
                  "#2563eb",

                color:
                  "#fff",

                fontWeight:
                  900,

                fontSize:
                  14,

                boxShadow:
                  "0 0 0 2px rgba(255,255,255,0.14)",
              }}
            >
              {getInitial(
                fullName
              )}
            </Avatar>

            <Box
              sx={{
                display: {
                  xs: "none",
                  sm: "block",
                },

                minWidth: 0,

                pr: 0.4,
              }}
            >
              <Typography
                noWrap

                sx={{
                  color:
                    "#fff",

                  fontSize:
                    12.5,

                  lineHeight:
                    1.15,

                  fontWeight:
                    900,
                }}
              >
                {fullName}
              </Typography>

              {showRoleLabel ? (
                <Typography
                  noWrap

                  sx={{
                    mt: 0.2,

                    color:
                      "#bfdbfe",

                    fontSize:
                      10.5,

                    lineHeight:
                      1.1,

                    fontWeight:
                      700,
                  }}
                >
                  {roleLabel}
                </Typography>
              ) : null}
            </Box>
          </Box>


          {/* ================================================= */}
          {/* LOGOUT */}
          {/* ================================================= */}

          <Button
            variant="contained"
            color="error"

            onClick={
              handleLogout
            }

            sx={{
              minWidth: {
                xs: 40,
                sm: 78,
                md: 92,
              },

              height: {
                xs: 38,
                sm: 40,
              },

              px: {
                xs: 0.8,
                sm: 1.25,
              },

              borderRadius:
                2,

              textTransform:
                "none",

              fontWeight:
                900,

              fontSize: {
                xs: 11,
                sm: 12,
              },

              boxShadow:
                "0 6px 14px rgba(220,38,38,0.20)",
            }}
          >
            <Box
              component="span"

              sx={{
                display: {
                  xs: "none",
                  sm: "inline",
                },
              }}
            >
              Quitter
            </Box>

            <Box
              component="span"

              sx={{
                display: {
                  xs: "inline",
                  sm: "none",
                },
              }}
            >
              ↪
            </Box>
          </Button>
        </Stack>
      </Toolbar>
    </AppBar>
  )
}


export default Topbar
